
import { defineComponent, computed, onMounted, onUnmounted, ref, ComponentPublicInstance } from "vue";
import store from "@/store";
import router from "@/router";
import NativeClient, { TitleInfo } from "@/client/native-client";
import { useApiErrorHandler } from "@/use/use-api-error-handler";
import { useApiErrorDialog } from "@/use/use-api-error-dialog";
import { useLog } from "@/use/use-log";
import { PAGE_ID } from "@/const/log-const";
import CommonRecipeList from "@/components/commonRecipe/CommonRecipeList.vue";
import CommonUtil, { isValidApiAccessToken } from "@/utils/common-utils";
import {
  MypageUserRanking,
  MypageFoodsRanking,
  MypageStoreUserRanking,
  MypageStoreFoodsRanking,
  MypageDialogInfo,
  HTMLDivElementEvent
} from "@/types/mypage";
import { ApiError, BeaconLogInfo, UpdatePageInfo } from "@/types/common";
import CommonDesignDialog from "@/components/common/CommonDesignDialog.vue";
import MypageFoodsRankingContent from "@/components/mypage/MypageFoodsRankingContent.vue";
import DateUtil from "@/utils/date-util";
import MypageSelectTabs from "@/components/mypage/MypageSelectTabs.vue";

export default defineComponent({
  name: "MypageMonthlyFoodsRankingDetailPage",
  components: {
    CommonDesignDialog,
    MypageSelectTabs
  },
  setup() {
    // mypage-report-detail?type=[used|disposed]&month=[yyyy-mm]

    const { setLogId, outputPageLogs } = useLog();

    // テキスト
    const userTitle = "あなた";
    const nationwideTitle = "全国";

    // タイトル行の設定
    const titleInfo = new TitleInfo(false, "使い切り食材ランキング");
    const type = router.currentRoute.value.query.type;
    if (type == "disposed") {
      titleInfo.title = "捨てた食材ランキング";
    }
    NativeClient.setTitle(titleInfo);
    CommonUtil.scroll({ top: 0, left: 0 });

    // 履歴/マイリスト洗濯状態の変数
    const currentMonthIndex = {
      get value() {
        const monthIndex: number | null = store.getters["mypage/getSelectedMonthIndex"];
        let realIndex = 5;
        if (monthIndex != null) {
          realIndex = monthIndex;
        }
        return realIndex;
      },
      set value(index: number) {
        store.dispatch("mypage/updateSelectedMonthIndex", index);
      }
    };

    // 表示する月を設定
    class MypageMonthlyFoodsRankingPageMonthData {
      month: string;
      displayMonth: string;
      constructor() {
        this.month = "";
        this.displayMonth = "";
      }
    }
    const monthInfoList: Array<MypageMonthlyFoodsRankingPageMonthData> = [];
    const currentDate = new Date();
    for (let index = -6; index <= 0; index ++) {
      const date = new Date(currentDate.getTime());
      date.setMonth(currentDate.getMonth() + index);
      const element = new MypageMonthlyFoodsRankingPageMonthData();
      element.month = DateUtil.dateYYYYMMWithHiphen(date);
      element.displayMonth = `${date.getFullYear()}年${date.getMonth()+1}月`;
      monthInfoList.push(element);
    }

    // 表示更新のためのリアクティブ
    const updateDisplayKey = ref(0);
    const updateDisplay = () => {
      if (updateDisplayKey.value == 100) {
        updateDisplayKey.value = 0;
      } else {
        updateDisplayKey.value++;
      }
    };

    const selectTabs = (index: number) => {
      NativeClient.sendDebugLog("MypageMonthlyFoodsRankingDetailPage selectTabs index = " + index);
    };

    //
    // API実行
    //
    // 月別ユーザー・ランキング取得
    const getMonthlyUserRanking = async (valid: boolean, yearMonth: string) => {
      NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage getMonthlyUserRanking()");
      const promise = new Promise((resolve) => {
        if (valid) {
          Promise.all([
            store.dispatch("mypage/getMonthlyUserRanking", yearMonth)
          ]).then((result) => {
            NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage getMonthlyUserRanking() complete");
            resolve(true);
          });
        } else {
          NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage getMonthlyUserRanking() no update");
          resolve(true);
        }
      });
      return promise;
    };
   // 月別個人食品消費ランキング取得
   const getPersonalMonthlyUsedFoodsRanking = async (valid: boolean, yearMonth: string) => {
      NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage getMonthlyUsedFoodsRanking()");
      const promise = new Promise((resolve) => {
        if (valid) {
          Promise.all([
            store.dispatch("mypage/getPersonalMonthlyUsedFoodsRanking", yearMonth)
          ]).then((result) => {
            NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage getMonthlyUsedFoodsRanking() complete");
            resolve(true);
          });
        } else {
          NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage getMonthlyUsedFoodsRanking() no update");
          resolve(true);
        }
      });
      return promise;
    };
    // 月別個人食品廃棄消費ランキング取得
    const getPersonalMonthlyDisposedFoodsRanking = async (valid: boolean, yearMonth: string) => {
      NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage getPersonalMonthlyDisposedFoodsRanking()");
      const promise = new Promise((resolve) => {
        if (valid) {
          Promise.all([
            store.dispatch("mypage/getPersonalMonthlyDisposedFoodsRanking", yearMonth)
          ]).then((result) => {
            NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage getPersonalMonthlyDisposedFoodsRanking() complete");
            resolve(true);
          });
        } else {
          NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage getPersonalMonthlyDisposedFoodsRanking() no update");
          resolve(true);
        }
      });
      return promise;
    };
    // 月別全国食品消費ランキング取得
    const getNationwideMonthlyUsedFoodsRanking = async (valid: boolean, yearMonth: string) => {
      NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage getNationwideMonthlyUsedFoodsRanking()");
      const promise = new Promise((resolve) => {
        if (valid) {
          Promise.all([
            store.dispatch("mypage/getNationwideMonthlyUsedFoodsRanking", yearMonth)
          ]).then((result) => {
            NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage getNationwideMonthlyUsedFoodsRanking() complete");
            resolve(true);
          });
        } else {
          NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage getNationwideMonthlyUsedFoodsRanking() no update");
          resolve(true);
        }
      });
      return promise;
    };
    // 月別個人食品廃棄消費ランキング取得
    const getNationwideMonthlyDisposedFoodsRanking = async (valid: boolean, yearMonth: string) => {
      NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage getNationwideMonthlyDisposedFoodsRanking()");
      const promise = new Promise((resolve) => {
        if (valid) {
          Promise.all([
            store.dispatch("mypage/getNationwideMonthlyDisposedFoodsRanking", yearMonth)
          ]).then((result) => {
            NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage getNationwideMonthlyDisposedFoodsRanking() complete");
            resolve(true);
          });
        } else {
          NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage getNationwideMonthlyDisposedFoodsRanking() no update");
          resolve(true);
        }
      });
      return promise;
    };
    // 一覧更新
    class MypageMonthlyFoodsRankingPageAPIExucuteInfo {
      userRanking: boolean;
      personalUsedFoodsRanking: boolean;
      personalDisposedFoodsRanking: boolean;
      nationwideUsedFoodsRanking: boolean;
      nationwideDisposedFoodsRanking: boolean;

      constructor() {
        this.userRanking = false;
        this.personalUsedFoodsRanking = false;
        this.personalDisposedFoodsRanking = false;
        this.nationwideUsedFoodsRanking = false;
        this.nationwideDisposedFoodsRanking = false;
      }
    } 
    const updateMonthList = async () => {
      NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage updateMonthList() monthInfoList = " + JSON.stringify(monthInfoList));
      const currentMonth = monthInfoList[currentMonthIndex.value].month;
      const executeInfo = new MypageMonthlyFoodsRankingPageAPIExucuteInfo();
      const userRanking = store.getters["mypage/getMonthlyUserRanking"](currentMonth);
      if (userRanking == null) {
        executeInfo.userRanking = true;
      }
      const personalUsedFoodsRanking = store.getters["mypage/getPersonalMonthlyUsedFoodsRanking"](currentMonth);
      if (personalUsedFoodsRanking == null) {
        executeInfo.personalUsedFoodsRanking = true;
      }
      const personalDisposedFoodsRanking = store.getters["mypage/getPersonalMonthlyDisposedFoodsRanking"](currentMonth);
      if (personalDisposedFoodsRanking == null) {
        executeInfo.personalDisposedFoodsRanking = true;
      }
      const nationwideUsedFoodsRanking = store.getters["mypage/getNationwideMonthlyUsedFoodsRanking"](currentMonth);
      if (nationwideUsedFoodsRanking == null) {
        executeInfo.nationwideUsedFoodsRanking = true;
      }
      const nationwideDisposedFoodsRanking = store.getters["mypage/getNationwideMonthlyDisposedFoodsRanking"](currentMonth);
      if (nationwideDisposedFoodsRanking == null) {
        executeInfo.nationwideDisposedFoodsRanking = true;
      }
      // if (executeInfo.userRanking || executeInfo.personalUsedFoodsRanking || executeInfo.personalDisposedFoodsRanking || executeInfo.nationwideUsedFoodsRanking || executeInfo.nationwideDisposedFoodsRanking) {
      //   store.dispatch("common/startLoading");
      //   NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage updateMonthList() userRanking = " + executeInfo.userRanking);
      //   NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage updateMonthList() personalUsedFoodsRanking = " + executeInfo.personalUsedFoodsRanking);
      //   NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage updateMonthList() personalDisposedFoodsRanking = " + executeInfo.personalDisposedFoodsRanking);
      //   NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage updateMonthList() nationwideUsedFoodsRanking = " + executeInfo.nationwideUsedFoodsRanking);
      //   NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage updateMonthList() nationwideDisposedFoodsRanking = " + executeInfo.nationwideDisposedFoodsRanking);
      //   await NativeClient.getApiParams();
      //   await Promise.all([
      //     getMonthlyUserRanking(executeInfo.userRanking, currentMonth),
      //     getPersonalMonthlyUsedFoodsRanking(executeInfo.personalUsedFoodsRanking, currentMonth),
      //     getPersonalMonthlyDisposedFoodsRanking(executeInfo.personalDisposedFoodsRanking, currentMonth),
      //     getNationwideMonthlyUsedFoodsRanking(executeInfo.nationwideUsedFoodsRanking, currentMonth),
      //     getNationwideMonthlyDisposedFoodsRanking(executeInfo.nationwideDisposedFoodsRanking, currentMonth)
      //   ]);
      //   store.dispatch("common/stopLoading");
      //   updateDisplay();
      // } else {
      //   NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage updateMonthList() no update month = " + currentMonth);
      // }
    };

    // スクロール
    const scroll = ref<ComponentPublicInstance<HTMLInputElement>>();
    const itemParent = ref<ComponentPublicInstance<HTMLElement>>();
    const setScrollTo = (targetPosition: number, isSmooth: boolean) => {
      NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage setScrollTo targetPosition = " + targetPosition + ", scrollWidth = " + scroll.value?.scrollWidth);
      // if (monthInfoList.length > 0) {
      //   const itemWidth = scroll.value!.scrollWidth / monthInfoList.length;
      //   if (isSmooth) {
      //     const itemParentBound = itemParent.value!.getBoundingClientRect();
      //     NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage setScrollTo itemParent itemParentBound.left = " + itemParentBound.left);
      //     // アニメーションでページ移動を行う
      //     const currentLeft = itemParentBound.left;
      //     const targetLeft = -1 * targetPosition * itemWidth;
      //     NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage setScrollTo currentLeft = " + currentLeft + ", targetLeft = " + targetLeft + ", itemWidth = " + itemWidth);
      //     const animationInstance = itemParent.value!.animate(
      //       [
      //         // 平行移動指定（移動元と移動先）
      //         { transform: `translateX(${currentLeft}px)` },
      //         { transform: `translateX(${targetLeft}px)` },
      //       ],
      //       {
      //         // タイミング、アニメーション後状態保持
      //         duration: 300,
      //         iterations: 1,
      //         fill: 'forwards'
      //       },
      //     );
      //     if (animationInstance != null) {
      //       animationInstance.onfinish = (ev: AnimationPlaybackEvent) => {
      //         NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage setScrollTo animation end");
      //       };
      //     }
      //   } else {
      //     scroll.value!.scrollTo({ top: 0, left: itemWidth * targetPosition });
      //   }
      // } else {
      //   NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage setScrollTo not valid element count");
      // }
    };
    const onScroll = (event: HTMLDivElementEvent) => {
      NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage onScroll event = " + JSON.stringify(event));
    };

    // 月別データ
    class MypageMonthlyFoodsRankingPageMonthlyData {
      month: string;
      updateKey: number;
      userRanking: MypageUserRanking | undefined;
      personalUsedFoodsRanking: MypageFoodsRanking | undefined;
      personalDisposedFoodsRanking: MypageFoodsRanking | undefined;
      nationwideUsedFoodsRanking: MypageFoodsRanking | undefined;
      nationwideDisposedFoodsRanking: MypageFoodsRanking | undefined;

      constructor() {
        this.month = "";
        this.updateKey = 0;
        this.userRanking = undefined;
        this.personalUsedFoodsRanking = undefined;
        this.personalDisposedFoodsRanking = undefined;
        this.nationwideUsedFoodsRanking = undefined;
        this.nationwideDisposedFoodsRanking = undefined;
      }
    } 
    const monthItemList = computed(() => {
      const result: Array<MypageMonthlyFoodsRankingPageMonthlyData> = [];
      for (let index = 0; index < 7; index ++) {
        const element = new MypageMonthlyFoodsRankingPageMonthlyData();
        element.month = monthInfoList[index].month;
        element.updateKey = updateDisplayKey.value;

        const userRanking: MypageStoreUserRanking | null = store.getters["mypage/getMonthlyUserRanking"](monthInfoList[index].month);
        if (userRanking != null && userRanking.data != null) {
          element.userRanking = userRanking.data;
        }
        const personalUsedFoodsRanking: MypageStoreFoodsRanking | null = store.getters["mypage/getPersonalMonthlyUsedFoodsRanking"](monthInfoList[index].month);
        if (personalUsedFoodsRanking != null && personalUsedFoodsRanking.data != null) {
          NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage monthItemList() month = " + monthInfoList[index].month + " personalUsedFoodsRanking = " + JSON.stringify(personalUsedFoodsRanking));
          element.personalUsedFoodsRanking = personalUsedFoodsRanking.data;
        }
        const personalDisposedFoodsRanking: MypageStoreFoodsRanking | null = store.getters["mypage/getPersonalMonthlyDisposedFoodsRanking"](monthInfoList[index].month);
        if (personalDisposedFoodsRanking != null && personalDisposedFoodsRanking.data != null) {
          element.personalDisposedFoodsRanking = personalDisposedFoodsRanking.data;
        }
        const nationwideUsedFoodsRanking: MypageStoreFoodsRanking | null = store.getters["mypage/getNationwideMonthlyUsedFoodsRanking"](monthInfoList[index].month);
        if (nationwideUsedFoodsRanking != null && nationwideUsedFoodsRanking.data != null) {
          element.nationwideUsedFoodsRanking = nationwideUsedFoodsRanking.data;
        }
        const nationwideDisposedFoodsRanking: MypageStoreFoodsRanking | null = store.getters["mypage/getNationwideMonthlyDisposedFoodsRanking"](monthInfoList[index].month);
        if (nationwideDisposedFoodsRanking != null && nationwideDisposedFoodsRanking.data != null) {
          element.nationwideDisposedFoodsRanking = nationwideDisposedFoodsRanking.data;
        }
        result.push(element);
      }
      NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage monthItemList() result = " + JSON.stringify(result));
      return result;
    });

    // ボタンの有効無効
    const isValidLeftButton = computed(() => {
      if (currentMonthIndex.value == 0) {
        return false;
      } else if (monthInfoList.length == 0) {
        return false;
      }
      return true;
    });
    const isValidRightButton = computed(() => {
      if (currentMonthIndex.value == 6) {
        return false;
      } else if (monthInfoList.length == 0) {
        return false;
      }
      return true;
    });

    // ヘルプのダイアログ
    const isShowHelpDialog = ref(false);
    const closeHelpDialog = () => {
      isShowHelpDialog.value = false;
    };

    const clickQuestion = () => {
      NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage clickQuestion");
      isShowHelpDialog.value = true;
    };
    const clickeUsedFoods = () => {
      NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage clickeUsedFoods");
    };
    const clickDisposedFoods = () => {
      NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage clickDisposedFoods");
    };

    // 表示中の月
    const currentMonth = computed(() => {
      NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage currentMonth");
      if (monthInfoList.length == 7) {
        NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage currentMonth monthInfoList set currentMonthIndex = " + currentMonthIndex.value);
        return monthInfoList[currentMonthIndex.value].displayMonth;
      }
      return "";
    });

    // 左右ボタン処理
    const clickLeft = () => {
      NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage clickLeft");
      if (currentMonthIndex.value > 0) {
        NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage clickLeft valid");
        setScrollTo(currentMonthIndex.value - 1, true);
        currentMonthIndex.value = currentMonthIndex.value - 1;

        // ページ移動でデータ取得
        updateMonthList();
      }
    };
    const clickRight = () => {
      NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage clickRight");
      if (currentMonthIndex.value < (monthInfoList.length - 1)) {
        NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage clickRight valid");
        setScrollTo(currentMonthIndex.value + 1, true);
        currentMonthIndex.value = currentMonthIndex.value + 1;

        // ページ移動でデータ取得
        updateMonthList();
      }
    };

    // 更新日時
    const updateDateText = computed(() => {
      //const month = monthInfoList[currentMonthIndex.value].month;
      // TODO:どのデータを参照するのか
      return "最終更新：2023/1/31 15:00";
    });

    // 本ページの表示／非表示
    onMounted(() => {
      NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage onMounted");
      const logInfo = new BeaconLogInfo(PAGE_ID.MYPAGE_USED_RANKING_USER, null, []);
      NativeClient.sendViewedLog(logInfo);
      setScrollTo(currentMonthIndex.value, false);
      updateMonthList();
    });
    onUnmounted(() => {
      NativeClient.sendDebugLog("MypageMonthlyFoodsRankingPage onUnmounted");
    });

    //
    // APIリクエスト時のエラーハンドル
    //
    const {
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage
    } = useApiErrorHandler();
    const { closeDialog } = useApiErrorDialog();
    const typeApiError = ref(9);
    const retryButtonTitle = "リトライ";
    const okButtonTitle = "OK";
    // APIのエラー・ダイアログは、リトライが存在しないが、共通ダイアログのIFは残っているため、削除せず、そのまま置いておく
    const retryApiErrorHandler = () => {
      NativeClient.sendDebugLog("MypagePage retryApiErrorHandler");
      closeDialog();

      // // 現在表示中の月のデータを再取得する
      // updateMonthList();
    };
    const okApiErrorHandler = () => {
      NativeClient.sendDebugLog("MypagePage okApiErrorHandler");
      closeDialog();
    };

    return {
      updateDisplayKey,
      userTitle,
      nationwideTitle,
      selectTabs,
      isValidLeftButton,
      isValidRightButton,
      currentMonth,
      isShowHelpDialog,
      closeHelpDialog,
      clickLeft,
      clickRight,
      clickQuestion,
      clickeUsedFoods,
      clickDisposedFoods,
      scroll,
      itemParent,
      onScroll,
      updateDateText,
      monthItemList,
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage,
      typeApiError,
      retryButtonTitle,
      okButtonTitle,
      retryApiErrorHandler,
      okApiErrorHandler
    };
  }
});
